import { Component, OnDestroy, OnInit } from '@angular/core';
import { TitleBarComponent } from '../shared/title-bar/title-bar.component';
import { StepperAllModule, StepperModule } from '@syncfusion/ej2-angular-navigations';
import { AppErrorMessagesComponent } from '../error-messages/app-error-messages.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RegisterFormType } from './register-form.type';
import { CustomValidatorsFactory } from '../shared/validators/custom-validators-factory';
import { homeRoute } from 'src/app/core/routing/route-names';
import { AddressAutofillComponent } from '../shared/address-autofill/address-autofill.component';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { startWith, Subscription } from 'rxjs';
import { LanguageService } from '../services/language.service';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { PasswordInputComponent } from '../shared/password-input/password-input.component';

import { CreateUserDTO } from '../models/models.swagger.generated';
import { RegisterService } from './register.service';

@Component({
  selector: 'app-register',
  imports: [TitleBarComponent, StepperAllModule, StepperModule, AppErrorMessagesComponent, TranslateModule, ReactiveFormsModule, AddressAutofillComponent, DropDownListModule, CheckBoxModule, PasswordInputComponent],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss'
})
export class RegisterComponent implements OnInit, OnDestroy {

  public stepIndex = 0;

  public registerForm: FormGroup<RegisterFormType>;

  public languageDropDowItems: any[] = [];
  public languageDropdownSettings = { text: 'value', value: 'key' };

  public homeRoute = homeRoute;

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private languageService: LanguageService,
    private registerService: RegisterService,
  ) { }

  public ngOnInit(): void {
    this.registerForm = this.fb.group<RegisterFormType>({
      email: this.fb.control<string>('', [Validators.required, Validators.email]),
      firstname: this.fb.control<string>('', [Validators.required]),
      lastname: this.fb.control<string>('', [Validators.required]),
      password: this.fb.control<string>('', [Validators.required, CustomValidatorsFactory.passwordValidation()]),
      confirmPassword: this.fb.control<string>('', [Validators.required, CustomValidatorsFactory.passwordMatchValidationForControl('password')]),
      fullAddress: this.fb.control<string>(null),
      addressCompletionId: this.fb.control<string>(null),
      addressAutocompleteSessionToken: this.fb.control<string>(null),
      phoneNumber: this.fb.control<string>(null, [CustomValidatorsFactory.createPhoneValidator()]),
      language: this.fb.control<string>('', [Validators.required])
    });

    // Re-validate confirmPassword when password changes
    this.registerForm.controls.password.valueChanges.subscribe(() => {
      this.registerForm.controls.confirmPassword.updateValueAndValidity();
    });

    this.updateLanguageDropdown();
    this.subscriptions.add(this.translate.onLangChange.pipe(startWith({})).subscribe(() => {
      this.updateLanguageDropdown();
    }));
  }

  private updateLanguageDropdown(): void {
    const langs = this.languageService.getLanguages();
    const translationKeys = langs.map(l => 'i18n.base.shared.Language.' + l);
    this.translate.get(translationKeys).subscribe(translations => {
      this.languageDropDowItems = langs.map(l => ({
        key: l,
        value: translations['i18n.base.shared.Language.' + l] || l
      }));
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public isFirstStepValid(): boolean {
    return this.registerForm &&
      this.registerForm.controls.email.valid &&
      this.registerForm.controls.firstname.valid &&
      this.registerForm.controls.lastname.valid &&
      this.registerForm.controls.password.valid &&
      this.registerForm.controls.confirmPassword.valid;
  }

  public goToNextStep() {
    this.stepIndex++;
  }

  public goToPreviousStep() {
    this.stepIndex--;
  }

  public submitForm(): void {
    this.registerService.registerUser(this.parseFormToDto()).subscribe({
      next: (result) => {
        this.stepIndex = 2;
      },
      error: (error) => {
        // Handle registration error
      }
    });
  }

  private parseFormToDto(): CreateUserDTO {
    const dto = new CreateUserDTO();
    dto.email = this.registerForm.controls.email.value;
    dto.firstname = this.registerForm.controls.firstname.value;
    dto.lastname = this.registerForm.controls.lastname.value;
    dto.password = this.registerForm.controls.password.value;
    dto.fullAddress = this.registerForm.controls.fullAddress.value;
    dto.addressCompletionId = this.registerForm.controls.addressCompletionId.value;
    dto.addressAutocompleteSessionToken = this.registerForm.controls.addressAutocompleteSessionToken.value;
    dto.phoneNumber = this.registerForm.controls.phoneNumber.value;
    dto.language = this.registerForm.controls.language.value;
    return dto;
  }
}
